import { DraggableList } from 'components/common';
import { ColumnToggle } from 'components/core';
import {
  getActiveToggleGroup,
  getFilterGroup,
  getToggleGroup,
  toggleGroupConfig,
  toggleGroupHeaderOverride,
} from 'components/core/ColumnPanel/utils';
import { useMemo } from 'react';

import { Box, Button, Flex, Icon, Typography } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

interface IColumnActivePanel {
  isOrder: boolean;
  toggleOptions: string[];
  visibleOptions: Array<{ id: string; label: string }>;
  onClose: () => void;
  onPress?: () => void;
  onSort: (columns: string[]) => void;
  handleToggle: (column: string) => void;
  handleToggleGroup: (column: string[], state: boolean) => void;
}

function ColumnActivePanel({
  isOrder,
  toggleOptions,
  visibleOptions,
  onClose,
  onPress,
  onSort,
  handleToggle,
  handleToggleGroup,
}: IColumnActivePanel) {
  const { t } = useTranslation();
  const { activeGroup, disabledList, dragList } = useMemo(() => {
    const activeGroup = getActiveToggleGroup(toggleOptions);
    const filterGroup = getFilterGroup();
    const toggleGroup = getToggleGroup();
    return {
      activeGroup,
      disabledList: visibleOptions.filter(({ id }) =>
        isOrder
          ? toggleOptions.includes(id)
          : toggleOptions.includes(id) &&
            activeGroup[id]?.length === toggleGroup[id]?.length &&
            !filterGroup.includes(id),
      ),
      dragList: visibleOptions.filter(({ id }) => {
        return isOrder
          ? !['order-number', ...toggleOptions].includes(id)
          : !(
              ['shipment-id', ...toggleOptions].includes(id) &&
              activeGroup[id]?.length === toggleGroup[id]?.length
            ) && !filterGroup.includes(id);
      }),
    };
  }, [isOrder, toggleOptions, visibleOptions]);

  return (
    <Flex orientation="vertical" css={{ height: '100%' }}>
      <Box css={{ flex: 1, overflow: 'scroll', padding: '$medium 0' }}>
        <Typography
          as="p"
          variant="body"
          css={{ color: '$text-tertiary', padding: '$medium' }}
        >
          {t('core.columnActivePanelDescription')}
        </Typography>
        <ColumnToggle
          id={isOrder ? 'order-number' : 'shipment-id'}
          activeToggleGroup={activeGroup}
          disableDrag={true}
          disableToggle={true}
          isOrder={isOrder}
          isSelected={true}
          isToggleGroup={false}
          label={isOrder ? t('core.orderNumber') : t('core.shipmentId')}
          toggleOptions={toggleOptions}
          // Always disabled until ID edit feature
          onToggle={() => false}
          onToggleGroup={() => false}
        />
        <DraggableList
          onChange={onSort}
          list={dragList.map(({ id, label }) => ({
            id,
            Component: (
              <ColumnToggle
                key={id}
                id={id}
                activeToggleGroup={activeGroup}
                disableDrag={false}
                disableToggle={false}
                isOrder={isOrder}
                isSelected={!toggleOptions.includes(id)}
                isToggleGroup={toggleGroupConfig.hasOwnProperty(id)}
                label={toggleGroupHeaderOverride[label as string] || label}
                onToggle={handleToggle}
                onToggleGroup={handleToggleGroup}
                toggleOptions={toggleOptions}
              />
            ),
          }))}
        />
        {disabledList.map(({ id, label }) => (
          <ColumnToggle
            key={id}
            id={id}
            activeToggleGroup={activeGroup}
            disableDrag={true}
            disableToggle={false}
            isOrder={isOrder}
            isSelected={!toggleOptions.includes(id)}
            isToggleGroup={toggleGroupConfig.hasOwnProperty(id)}
            label={toggleGroupHeaderOverride[label] || label}
            onToggle={handleToggle}
            onToggleGroup={handleToggleGroup}
            toggleOptions={toggleOptions}
          />
        ))}
      </Box>
      <Flex
        align="center"
        css={{
          borderTop: '1px solid $border-primary',
          padding: '13px $medium', // match table footer
        }}
        justify={onPress ? 'between' : 'end'}
      >
        {onPress && (
          <Button
            children={t('core.addReferenceKey')}
            css={{ color: '$palette-indigo-600' }}
            onPress={onPress}
            size="medium"
            startIcon={<Icon icon="add" />}
            variant="tertiary"
          />
        )}
        <Button size="medium" onPress={onClose}>
          {t('global.close')}
        </Button>
      </Flex>
    </Flex>
  );
}

export default ColumnActivePanel;
